import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { api, fmt, errText } from "@/lib/api";
import { shortDate } from "@/lib/partners";
import { leadName } from "@/lib/leads";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Plus, Eye, FilePlus2, Sparkles, Inbox } from "lucide-react";

const STATUS_STYLES = {
  Draft: "bg-gray-100 text-gray-600",
  Sent: "bg-blue-50 text-blue-700",
  Viewed: "bg-indigo-50 text-indigo-700",
  "Package Selected": "bg-amber-50 text-amber-700",
  "Invoice Created": "bg-[#c19a6b]/15 text-[#8a6a42]",
  "Deposit Paid": "bg-emerald-50 text-emerald-700",
  "Paid in Full": "bg-emerald-600 text-white",
  Expired: "bg-red-50 text-red-600",
  Closed: "bg-gray-200 text-gray-500",
};

const PKG = { essential: "Essential", complete: "Complete", signature: "Signature", partner: "Signature · Private" };

export default function Dashboard() {
  const [quotes, setQuotes] = useState(null);
  const [leads, setLeads] = useState([]);
  const [revivalLeads, setRevivalLeads] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = useCallback((s) => {
    api.get("/quotes", { params: { search: s || "" } }).then((r) => setQuotes(r.data)).catch(() => setQuotes([]));
  }, []);

  const loadLeads = useCallback(() => {
    api.get("/leads", { params: { status: "New" } }).then((r) => setLeads(r.data)).catch(() => setLeads([]));
    api.get("/revival-leads", { params: { status: "New Lead", limit: 6 } }).then((r) => setRevivalLeads(r.data)).catch(() => setRevivalLeads([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  useEffect(loadLeads, [loadLeads]);

  const convertLead = async (leadId) => {
    try {
      const { data } = await api.post(`/leads/${leadId}/convert`);
      toast.success("Quote created — finish pricing and send it");
      navigate(`/admin/quotes/${data.quote_id}`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const convertRevivalLead = async (leadId) => {
    try {
      const { data } = await api.post(`/revival-leads/${leadId}/convert`);
      toast.success("Quote created — finish pricing and send it");
      navigate(`/admin/quotes/${data.quote_id}`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");

  return (
    <AdminLayout title="Quotes">
      {revivalLeads.length > 0 && (
        <div className="bg-white border border-gray-200 border-l-4 border-l-[#c19a6b] p-6 sm:p-8 mb-8" data-testid="revival-leads-panel">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#8a6a42] font-bold flex items-center gap-2">
              <Inbox size={14} /> New Revival leads from the landing page
            </p>
            <Link to="/admin/leads" data-testid="dashboard-all-leads-link" className="text-[11px] uppercase tracking-[0.18em] text-gray-500 hover:text-black underline underline-offset-4">
              All leads
            </Link>
          </div>
          <div className="space-y-3">
            {revivalLeads.map((l) => (
              <div key={l.id} data-testid={`dashboard-revival-lead-${l.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between border-b border-gray-100 pb-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">
                    {leadName(l)}
                    <span className="text-gray-400 font-normal"> · {l.lead_number}</span>
                    <span className={`ml-2 align-middle text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${l.qualification === "Potential Restoration Lead" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
                      {l.qualification}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {[l.furniture_types?.join(", "), l.colour_goal_label, l.timeline_label, `${l.photo_count} photo${l.photo_count === 1 ? "" : "s"}`, shortDate(l.created_at)].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button asChild variant="outline" className="h-10 rounded-sm text-[11px] uppercase tracking-wider">
                    <Link to={`/admin/leads/${l.id}`} data-testid={`dashboard-review-lead-${l.id}`}>Review</Link>
                  </Button>
                  <Button data-testid={`dashboard-convert-revival-lead-${l.id}`} onClick={() => convertRevivalLead(l.id)} className="h-10 bg-[#c19a6b] hover:bg-[#b08a5c] text-white rounded-sm text-[11px] uppercase tracking-wider btn-lift">
                    <FilePlus2 size={13} className="mr-1.5" /> Create Quote
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {leads.length > 0 && (
        <div className="bg-[#0B0B0B] text-white p-6 sm:p-8 mb-8" data-testid="partner-leads-panel">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#c19a6b] font-bold flex items-center gap-2">
              <Sparkles size={14} /> New private client leads
            </p>
            <Link to="/admin/realtors" className="text-[11px] uppercase tracking-[0.18em] text-white/50 hover:text-white underline underline-offset-4">
              Realtor partners
            </Link>
          </div>
          <div className="space-y-3">
            {leads.slice(0, 5).map((l) => (
              <div key={l.id} data-testid={`dashboard-lead-${l.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between border-b border-white/10 pb-3">
                <div>
                  <p className="text-sm font-semibold">
                    {l.customer.first_name} {l.customer.last_name}
                    <span className="text-white/40 font-normal"> · lead generated by {l.realtor.display_name}</span>
                  </p>
                  <p className="text-xs text-white/45 mt-1">
                    {[l.customer.email, l.customer.city, l.moving ? `Moving${l.move_date ? ` · ${l.move_date}` : ""}` : null, shortDate(l.created_at)]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {l.pieces && <p className="text-xs text-white/60 mt-1.5">{l.pieces}</p>}
                </div>
                <Button
                  data-testid={`dashboard-convert-lead-${l.id}`}
                  onClick={() => convertLead(l.id)}
                  className="h-10 bg-[#c19a6b] hover:bg-[#b08a5c] text-white rounded-sm text-[11px] uppercase tracking-wider btn-lift shrink-0"
                >
                  <FilePlus2 size={13} className="mr-1.5" /> Create Quote
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-8">
        <div className="relative w-full sm:w-96">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="quote-search-input"
            placeholder="Search customer, quote number, project..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-11 rounded-sm bg-white"
          />
        </div>
        <Button
          data-testid="create-quote-btn"
          asChild
          className="bg-black hover:bg-black/85 text-white rounded-sm h-11 px-6 tracking-[0.1em] uppercase text-xs btn-lift"
        >
          <Link to="/admin/quotes/new">
            <Plus size={16} className="mr-1" /> Create New Quote
          </Link>
        </Button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="quotes-table">
          <thead>
            <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
              <th className="px-4 py-3 font-medium">Quote #</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Project</th>
              <th className="px-4 py-3 font-medium">Realtor</th>
              <th className="px-4 py-3 font-medium">Recommended</th>
              <th className="px-4 py-3 font-medium text-right">Amount</th>
              <th className="px-4 py-3 font-medium">Selected</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Views</th>
              <th className="px-4 py-3 font-medium">Last Viewed</th>
              <th className="px-4 py-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {quotes === null && (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">Loading...</td></tr>
            )}
            {quotes?.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-16 text-center text-gray-400" data-testid="no-quotes-message">
                No quotes yet. Create your first quote to get started.
              </td></tr>
            )}
            {quotes?.map((q) => (
              <tr
                key={q.id}
                data-testid={`quote-row-${q.quote_number}`}
                onClick={() => navigate(`/admin/quotes/${q.id}`)}
                className="border-b border-gray-100 hover:bg-[#FAF7F2] cursor-pointer transition-colors duration-150"
              >
                <td className="px-4 py-3.5 font-semibold whitespace-nowrap">{q.quote_number}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">{q.customer_name}</td>
                <td className="px-4 py-3.5 max-w-[180px] truncate">{q.project_title}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">
                  {q.realtor_name ? (
                    <span className="text-[#8a6a42]" data-testid={`quote-realtor-${q.quote_number}`}>
                      {q.realtor_name}
                      {q.partner_pricing && <span className="text-[10px] uppercase tracking-[0.15em] text-gray-400 ml-1.5">private rate</span>}
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3.5">{PKG[q.recommended_package]}</td>
                <td className="px-4 py-3.5 text-right font-semibold whitespace-nowrap">{fmt(q.amount_cents)}</td>
                <td className="px-4 py-3.5">{q.selected_package ? PKG[q.selected_package] : "—"}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">
                  <span className={`text-[11px] px-2 py-1 font-semibold tracking-wide ${STATUS_STYLES[q.status] || "bg-gray-100"}`}>
                    {q.status}
                  </span>
                </td>
                <td className="px-4 py-3.5">
                  <span className="inline-flex items-center gap-1 text-gray-500"><Eye size={13} /> {q.view_count}</span>
                </td>
                <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{fmtDate(q.last_viewed_at)}</td>
                <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{fmtDate(q.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
